import { toast, type ToastOptions } from 'vue3-toastify'
import { roundDatesToNearest30Minutes } from '~/utils/dateHelpers'
import { useDateRequestFormStorage } from '~/composables/useDateRequestFormStorage'
import { useDateRequestTracking } from '~/composables/useDateRequestTracking'
import type { EventDateType } from '~/composables/types/event'

export interface DateRequestForm {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

export interface DateRequestOptions {
  /** Omitted for private requests not opened from an event PDP (no PE threshold validation). */
  eventDescriptionId?: string
  form: Ref<DateRequestForm> | ComputedRef<DateRequestForm>
  isPrivateEvent?: boolean
  privateDateTypeChecked?: Ref<boolean>
  onSuccess?: (dateRequestId?: string) => void
  onError?: (error: any) => void
}

export const useDateRequest = () => {
  const { t } = useI18n()
  const { saveFormData } = useDateRequestFormStorage()
  const { addDateRequestPage } = useDateRequestTracking()
  const localePath = useLocalePath()
  const subdomain = useSubdomain()
  const {
    dateRequestSidebarRequestLocation,
    dateRequestSidebarRequestPreferredLocation,
  } = useUiState()
  const eventData = useEventData()
  const requestEventDates = async (options: DateRequestOptions) => {
    const {
      eventDescriptionId,
      form,
      isPrivateEvent = false,
      privateDateTypeChecked,
      onSuccess,
      onError,
    } = options

    const loading = ref(false)
    loading.value = true

    try {
      // Get the current form value
      const formValue = toValue(form)

      // Normalize preferred/backup times to :00/:30 before building the payload.
      // This is the authoritative guard: it catches odd times from every picker
      // and device (iOS native datetime-local, desktop vue-datepicker, typed input).
      formValue.dates = roundDatesToNearest30Minutes(formValue.dates)

      const cart_value =
        (Number(formValue.numberOfTickets) *
          Number(
            eventGetters.getDefaultPriceAmount(eventData.value) || 10000
          )) /
        100

      if (isPrivateEvent) {
        // Private event request
        const response = await kftApiRequest(
          '/v1/store/private-event-requests',
          {
            body: {
              company_name: formValue?.companyName,
              city: formValue?.city,
              ...(eventDescriptionId
                ? { event_description_id: eventDescriptionId }
                : {}),
              email: formValue.email,
              first_name: formValue.name,
              phone: formValue.phone,
              last_name: formValue.surname,
              number_of_tickets: Number(formValue.numberOfTickets),
              date: formValue.dates[0]
                ? formValue.dates[0]?.replace('T', ' ') + ':00'
                : undefined,
              backup_date_1: formValue.dates[1]
                ? formValue.dates[1]?.replace('T', ' ') + ':00'
                : undefined,
              backup_date_2: formValue.dates[2]
                ? formValue.dates[2]?.replace('T', ' ') + ':00'
                : undefined,
              last_page_seen: window.location.href,
              ...(dateRequestSidebarRequestPreferredLocation.value
                ? {
                    preferred_location:
                      dateRequestSidebarRequestPreferredLocation.value,
                  }
                : {}),
              more_info: formValue.additionalInfo,
            },
          }
        )

        const location =
          (toValue(dateRequestSidebarRequestLocation) as any) || ''
        sendPrivateEventRequestForm(
          location,
          cart_value,
          (response as any)?.data?.sha256_email_address
        )

        // Track the private event date request
        const privateEventRequestId = (response as any)?.data?.id
        if (privateEventRequestId && eventDescriptionId && eventData.value) {
          const eventPermalink = eventGetters.getPermalink(eventData.value)
          const eventUrl = localePath(`/e/${eventPermalink}`)
          const principalImage = eventGetters.getPrincipalImage(eventData.value)

          addDateRequestPage(
            privateEventRequestId,
            eventDescriptionId,
            eventUrl,
            formValue.dates.filter((date) => date !== ''),
            {
              title: eventGetters.getTitle(eventData.value),
              image: principalImage?.cropped || principalImage?.thumb,
              location: eventGetters.getHasMultipleLocations(eventData.value)
                ? eventGetters.getAddressLocalityName(eventData.value)
                : eventGetters.getFormattedAddress(eventData.value) ||
                  eventGetters.getAddressLocalityName(eventData.value),
              price: eventGetters.getDefaultPriceAmount(eventData.value),
              priceCurrency:
                eventGetters.getDefaultPriceCurrency(eventData.value) || 'EUR',
            },
            {
              numberOfTickets: Number(formValue.numberOfTickets),
              isPrivateEvent: true,
            }
          )
        }
      } else {
        // Regular date request
        const response = await kftApiRequest('/v1/store/date-requests', {
          body: {
            event_description_id: eventDescriptionId,
            email: formValue.email,
            first_name: formValue.name,
            phone: formValue.phone,
            last_name: formValue.surname,
            number_of_tickets: Number(formValue.numberOfTickets),
            date_type:
              formValue.dateType === 'PRIVATE' ||
              formValue.dateType === 'PUBLIC'
                ? formValue.dateType
                : privateDateTypeChecked?.value
                  ? 'PRIVATE'
                  : 'PUBLIC',
            last_page_seen: window.location.href,
            dates: formValue.dates
              .filter((date) => date !== '')
              .map((date) => date.replace('T', ' ')),
            ...(subdomain.value ? { external: true } : {}),
          },
        })

        const location =
          (toValue(dateRequestSidebarRequestLocation) as any) || ''
        sendDateRequestForm(
          location,
          cart_value,
          (response as any)?.data?.sha256_email_address
        )

        // Save form data to local storage after successful submission
        saveFormData({
          ...formValue,
          dateType:
            formValue.dateType === 'PRIVATE' || formValue.dateType === 'PUBLIC'
              ? formValue.dateType
              : privateDateTypeChecked?.value
                ? 'PRIVATE'
                : 'PUBLIC',
        })

        // Pass the date request ID to the success callback
        const dateRequestId = (response as any)?.data?.id

        // Track the date request
        if (dateRequestId && eventData.value) {
          const eventPermalink = eventGetters.getPermalink(eventData.value)
          const eventUrl = localePath(`/e/${eventPermalink}`)
          const principalImage = eventGetters.getPrincipalImage(eventData.value)
          const dateType =
            formValue.dateType === 'PRIVATE' || formValue.dateType === 'PUBLIC'
              ? formValue.dateType
              : privateDateTypeChecked?.value
                ? 'PRIVATE'
                : 'PUBLIC'

          addDateRequestPage(
            dateRequestId,
            eventDescriptionId,
            eventUrl,
            formValue.dates.filter((date) => date !== ''),
            {
              title: eventGetters.getTitle(eventData.value),
              image: principalImage?.cropped || principalImage?.thumb,
              location: eventGetters.getHasMultipleLocations(eventData.value)
                ? eventGetters.getAddressLocalityName(eventData.value)
                : eventGetters.getFormattedAddress(eventData.value) ||
                  eventGetters.getAddressLocalityName(eventData.value),
              price: eventGetters.getDefaultPriceAmount(eventData.value),
              priceCurrency:
                eventGetters.getDefaultPriceCurrency(eventData.value) || 'EUR',
            },
            {
              dateType,
              numberOfTickets: Number(formValue.numberOfTickets),
            }
          )
        }

        onSuccess?.(dateRequestId)
        return
      }

      // Save form data to local storage after successful submission
      saveFormData({
        ...formValue,
        dateType:
          formValue.dateType === 'PRIVATE' || formValue.dateType === 'PUBLIC'
            ? formValue.dateType
            : privateDateTypeChecked?.value
              ? 'PRIVATE'
              : 'PUBLIC',
      })

      onSuccess?.()

      toast(
        t('subscribeToEventUpdatesModal.secondStep.requestDatesSuccessMessage'),
        {
          autoClose: 2000,
          type: 'success',
          position: toast.POSITION.TOP_CENTER,
          multiple: false,
        } as ToastOptions
      )
    } catch (e: any) {
      if (onError) {
        onError(e)
        return
      }
      const errorMessage = e.data?.message || t('general.error')
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  return {
    requestEventDates,
  }
}
